import React, { memo, useCallback, useMemo } from 'react';
import { ScrollView, } from 'react-native';
import { ModalScrollView } from 'design-system/ModalScrollables';
import { IconGoal, IconText, IconBank, IconTrash, IconMoneyBill, IconBoldImage, IconCoins, IconSmartRule, IconPaperDownload, IconPaperUpload, } from 'design-system/icons';
import IconBlock from 'design-system/icons/IconBlock';
import PopUpMenuModal from 'design-system/PopUpMenuModal.web';
import { rem, SCROLL_INDICATOR_INSETS, SettingsItem } from 'design-system';
import useStyles from 'hooks/useStyles';
import Modal from 'utils/packages/Modal';
import { assertUnreachable } from 'utils';
import { useScrollableModalMaxHeight } from 'hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import { showExportDataMethodModal } from 'features/export-data/ExportDataMethodModal';
import { selectHasLiveExportFeature } from 'features/export-data-live/selectors';
import { DUMMY_ACCOUNT_ICON_MAP } from 'features/accounts/hooks/useDummyAccountLogo';
import { useSmartRulesNavigation } from 'hooks/useSmartRulesNavigation';
import IconRepeat from 'design-system/icons/IconRepeat';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectShouldShowCsvImports } from 'features/accounts/hooks/selectors';
import { selectShowNewPhysicalAssets } from 'features/accounts/selectors';
import { getPropertyName, PHYSICAL_ASSET_TYPES, } from 'features/accounts/constants';
import IconEyeSlash from 'design-system/icons/IconEyeSlash';
import IconEye from 'design-system/icons/IconEye';
import AccountLinkedToAutosaveModal from 'features/settings/logins/components/AccountLinkedToAutosaveModal';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { isWeb } from '../../../../constants';
import AccountTypeMenu from './AccountTypeMenu';
import { changeableAccountTypes } from './accountType';
import { useHideAccount } from '../../hooks/useHideAccount';
import { useCloseAccount } from '../../hooks/useCloseAccount';
import { useDeleteAccount } from '../../hooks/useDeleteAccount';
import { AccountMenuCard } from './AccountMenuCard';
var MenuItemType;
(function (MenuItemType) {
    MenuItemType["Export"] = "EXPORT";
    MenuItemType["Import"] = "IMPORT";
    MenuItemType["Saving"] = "SAVING";
    MenuItemType["SavingEdit"] = "SAVING_EDIT";
    MenuItemType["Hide"] = "HIDE";
    MenuItemType["Close"] = "CLOSED";
    MenuItemType["Rename"] = "RENAME";
    MenuItemType["ChangeType"] = "TYPE";
    MenuItemType["Overdraft"] = "OVERDRAFT";
    MenuItemType["CloseManual"] = "MANUAL_CLOSE";
    MenuItemType["RenameManual"] = "MANUAL_RENAME";
    MenuItemType["UpdateLogo"] = "UPDATE_LOGO";
    MenuItemType["ChangeBalance"] = "MANUAL_BALANCE";
    MenuItemType["DeleteManual"] = "MANUAL_DELETE";
    MenuItemType["SmartRule"] = "SMART_RULE";
})(MenuItemType || (MenuItemType = {}));
const ModalToUse = isWeb ? PopUpMenuModal : Modal;
const AccountMenu = ({ account, navigation, navigateBackAfterDelete = true, onScroll, }) => {
    const styles = useStyles(styleSet);
    const goals = useAppSelector((store) => store.accounts.goals);
    const savingsPlanAccountId = useAppSelector((state) => state.pots.savingsPlan?.bankInfo?.accountId);
    const shouldShowCsvImports = useAppSelector(selectShouldShowCsvImports);
    const hasLiveExportFeature = useAppSelector(selectHasLiveExportFeature);
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const isAccountLinkedToAutosave = savingsPlanAccountId === account.id;
    const isPhysicalAsset = useMemo(() => PHYSICAL_ASSET_TYPES.has(account.type), [account.type]);
    const propertyName = getPropertyName(account.type);
    const config = useMemo(() => [
        {
            id: MenuItemType.Export,
            text: 'Export transaction data',
            icon: <IconPaperDownload gradientKey="darkPurple"/>,
            sortingOrder: 0,
        },
        ...(shouldShowCsvImports
            ? [
                {
                    id: MenuItemType.Import,
                    text: 'Import transaction data',
                    icon: <IconPaperUpload gradientKey="darkPink"/>,
                    manual: true,
                    notOnTypes: ['REAL_ESTATE', 'VEHICLE', 'OTHER'],
                    sortingOrder: 0,
                },
            ]
            : []),
        {
            id: MenuItemType.CloseManual,
            text: 'Mark as closed',
            icon: <IconBlock colorKey="statusNegative"/>,
            notOnTypes: ['REAL_ESTATE', 'VEHICLE', 'OTHER'],
            manual: true,
            closed: false,
            sortingOrder: 10,
        },
        {
            id: MenuItemType.CloseManual,
            text: 'Mark as open',
            icon: <IconRepeat colorKey="statusPositive"/>,
            notOnTypes: ['REAL_ESTATE', 'VEHICLE', 'OTHER'],
            manual: true,
            closed: true,
            sortingOrder: 10,
        },
        {
            id: MenuItemType.Saving,
            text: 'Set a goal',
            icon: <IconGoal gradientKey="skyBlue"/>,
            closed: false,
            types: ['SAVINGS', 'INVESTMENT'],
            goals: false,
            sortingOrder: 0,
        },
        {
            id: MenuItemType.SavingEdit,
            text: 'Edit goal',
            icon: <IconGoal gradientKey="skyBlue"/>,
            closed: false,
            types: ['SAVINGS', 'INVESTMENT'],
            goals: true,
            sortingOrder: 0,
        },
        {
            id: MenuItemType.RenameManual,
            text: 'Rename',
            icon: <IconText />,
            closed: false,
            manual: true,
            sortingOrder: 1,
        },
        {
            id: MenuItemType.UpdateLogo,
            text: 'Update logo',
            icon: <IconBoldImage />,
            closed: false,
            manual: true,
            sortingOrder: 2,
        },
        {
            id: MenuItemType.ChangeBalance,
            text: isPhysicalAsset && showNewPhysicalAssets
                ? 'Update valuation'
                : 'Change balance',
            icon: <IconCoins width={24} height={24} alt gradientKey="yellow"/>,
            closed: false,
            manual: true,
            sortingOrder: 3,
        },
        {
            id: MenuItemType.SmartRule,
            text: 'Set a smart rule',
            icon: <IconSmartRule />,
            closed: false,
            manual: true,
            notOnTypes: ['REAL_ESTATE', 'VEHICLE', 'OTHER'],
            sortingOrder: 5,
            showOnWeb: false,
        },
        {
            id: MenuItemType.DeleteManual,
            text: isPhysicalAsset && showNewPhysicalAssets
                ? `Delete this ${propertyName.toLowerCase()}`
                : 'Delete this account',
            icon: <IconTrash colorKey="statusNegative"/>,
            closed: false,
            manual: true,
            sortingOrder: 9,
        },
        {
            id: MenuItemType.Close,
            text: 'Mark as closed',
            icon: <IconBlock colorKey="statusNegative"/>,
            closed: false,
            manual: false,
            sortingOrder: 10,
        },
        {
            id: MenuItemType.Close,
            text: 'Mark as open',
            icon: <IconRepeat colorKey="statusPositive"/>,
            closed: true,
            manual: false,
            sortingOrder: 10,
        },
        {
            id: MenuItemType.Hide,
            text: 'Hide',
            icon: <IconEyeSlash colorKey="statusOffline"/>,
            manual: false,
            hidden: false,
            sortingOrder: 6,
        },
        {
            id: MenuItemType.Hide,
            text: 'Unhide',
            icon: <IconEye colorKey="statusPositive"/>,
            manual: false,
            hidden: true,
            sortingOrder: 6,
        },
        {
            id: MenuItemType.Rename,
            text: 'Rename',
            icon: <IconText />,
            closed: false,
            manual: false,
            sortingOrder: 1,
        },
        {
            id: MenuItemType.ChangeType,
            text: isPhysicalAsset && showNewPhysicalAssets
                ? `Change ${getPropertyName(account.type).toLowerCase()} type`
                : 'Change account type',
            icon: DUMMY_ACCOUNT_ICON_MAP[account.type] || (<IconBank gradientKey="lightPurple"/>),
            closed: false,
            sortingOrder: 4,
            types: changeableAccountTypes,
        },
        {
            id: MenuItemType.Overdraft,
            text: 'Set overdraft',
            icon: <IconMoneyBill gradientKey="skyBlue"/>,
            closed: false,
            manual: false,
            sortingOrder: 5,
            types: ['CHECKING'],
        },
    ], [
        shouldShowCsvImports,
        isPhysicalAsset,
        showNewPhysicalAssets,
        propertyName,
        account.type,
    ]);
    const options = useMemo(() => {
        const { isClosed, isHidden, provider, proPreviewAccount, ultimatePreviewAccount, } = account;
        const { type } = account;
        const isGoal = Boolean(goals[account.id]);
        const isManual = provider === 'MANUAL';
        const filteredItems = config.filter((menuItem) => {
            if (proPreviewAccount && isClosed && isManual && menuItem.manual) {
                // For downgraded manual accounts we only allow deletion not reopening
                if (menuItem.id === MenuItemType.CloseManual) {
                    return false;
                }
                if (menuItem.id === MenuItemType.DeleteManual) {
                    return true;
                }
            }
            if (ultimatePreviewAccount) {
                return menuItem.hidden === isHidden;
            }
            if (menuItem.showOnWeb !== undefined) {
                if (!menuItem.showOnWeb && isWeb) {
                    return false;
                }
            }
            if (menuItem.manual !== undefined) {
                if (menuItem.manual !== isManual) {
                    return false;
                }
            }
            if (menuItem.closed !== undefined) {
                if (menuItem.closed !== isClosed) {
                    return false;
                }
            }
            if (menuItem.goals !== undefined) {
                if (menuItem.goals !== isGoal) {
                    return false;
                }
            }
            if (menuItem.hidden !== undefined) {
                if (menuItem.hidden !== isHidden) {
                    return false;
                }
            }
            if (menuItem.notOnTypes) {
                if (menuItem.notOnTypes.findIndex((t) => t === type) !== -1) {
                    return false;
                }
            }
            if (menuItem.types) {
                if (menuItem.types.findIndex((t) => t === type) === -1) {
                    return false;
                }
            }
            return true;
        });
        filteredItems.sort((a, b) => a.sortingOrder - b.sortingOrder);
        return filteredItems;
    }, [account, config, goals]);
    const handleToggleClosed = useCloseAccount();
    const handleToggleHidden = useHideAccount();
    const deleteAccount = useDeleteAccount();
    const { onPressSmartRules } = useSmartRulesNavigation();
    const onOptionPressed = useCallback((data) => {
        if (!data)
            return;
        switch (data) {
            case MenuItemType.Export:
                ModalToUse.hide(() => {
                    const exportDataParams = { accountIds: [account.id] };
                    if (hasLiveExportFeature) {
                        showExportDataMethodModal({ exportDataParams });
                    }
                    else {
                        navigation.navigateDeprecated('ExportData', exportDataParams);
                    }
                });
                break;
            case MenuItemType.Import:
                ModalToUse.hide(() => navigation.navigateDeprecated('DummyAccountImportTransactions', {
                    id: account.id,
                    type: account.type,
                    spaceId: account.spaceId,
                }));
                break;
            case MenuItemType.Saving:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('SavingGoalEdit', {
                        accountId: account.id,
                    });
                });
                break;
            case MenuItemType.SavingEdit:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('SavingGoalEdit', {
                        accountId: account.id,
                        goalId: goals?.[account.id]?.id,
                    });
                });
                break;
            case MenuItemType.Rename:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('EditAccountName', {
                        id: account.id,
                    });
                });
                break;
            case MenuItemType.Hide: {
                ModalToUse.hide(() => {
                    handleToggleHidden(account, () => {
                        if (navigateBackAfterDelete) {
                            navigation.goBack();
                        }
                    });
                });
                break;
            }
            case MenuItemType.ChangeType:
                if (account) {
                    if (isAccountLinkedToAutosave) {
                        ModalToUse.hide(() => AccountLinkedToAutosaveModal.show(() => {
                            if (isWeb) {
                                UnavailableOnWebModal.show('emma://auto-save', 'Manage Autosave');
                            }
                            else {
                                navigation.navigate('AutoSave');
                            }
                        }, 'changeType'));
                        break;
                    }
                    ModalToUse.hide(() => {
                        Modal.show(<AccountTypeMenu accountId={account.id} currentAccountType={account.type} isManual={account.provider === 'MANUAL'}/>, {
                            title: isPhysicalAsset && showNewPhysicalAssets
                                ? 'What property is this?'
                                : 'What account is this?',
                            containerStyle: styles.modalContentContainer,
                            lightBg: true,
                        });
                    });
                }
                break;
            case MenuItemType.Overdraft:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('EditOverdraft', { id: account.id });
                });
                break;
            case MenuItemType.Close:
            case MenuItemType.CloseManual: {
                ModalToUse.hide(() => {
                    handleToggleClosed(account, () => {
                        if (navigateBackAfterDelete) {
                            navigation.goBack();
                        }
                    });
                });
                break;
            }
            case MenuItemType.UpdateLogo:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('DummyAccountLogoType', {
                        id: account.id,
                        isChangingLogo: true,
                    });
                });
                break;
            case MenuItemType.RenameManual:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('DummyAccountSetup', {
                        id: account.id,
                        isChangingName: true,
                    });
                });
                break;
            case MenuItemType.ChangeBalance:
                ModalToUse.hide(() => {
                    navigation.navigateDeprecated('AddBalance', {
                        id: account.id,
                        isChangingBalance: true,
                    });
                });
                break;
            case MenuItemType.DeleteManual:
                ModalToUse.hide(() => {
                    Modal.showConfirmation({
                        title: isPhysicalAsset && showNewPhysicalAssets
                            ? `Delete this ${propertyName.toLowerCase()}`
                            : 'Delete account',
                        subtitle: `Would you like to delete this ${isPhysicalAsset && showNewPhysicalAssets
                            ? propertyName.toLowerCase()
                            : 'account'}?`,
                        confirmButtonTitle: 'Yes, delete',
                        cancelButtonTitle: 'No, keep it',
                        onConfirm: () => {
                            deleteAccount(account);
                            if (navigateBackAfterDelete) {
                                navigation.goBack();
                            }
                        },
                    });
                });
                break;
            case MenuItemType.SmartRule:
                ModalToUse.hide(() => onPressSmartRules(true));
                break;
            default:
                assertUnreachable(data);
                break;
        }
    }, [
        account,
        deleteAccount,
        goals,
        handleToggleClosed,
        handleToggleHidden,
        hasLiveExportFeature,
        isAccountLinkedToAutosave,
        isPhysicalAsset,
        navigateBackAfterDelete,
        onPressSmartRules,
        propertyName,
        showNewPhysicalAssets,
        styles.modalContentContainer,
    ]);
    const content = useMemo(() => (<>
        <AccountMenuCard account={account} ModalToUse={ModalToUse}/>
        {options.map((item) => (<SettingsItem key={item.id} icon={item.icon} title={item.text} style={item.style} disabled={!!item.disabled} description={item.description} onPress={() => onOptionPressed(item.id)}/>))}
      </>), [account, onOptionPressed, options]);
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    const paddingBottom = useMarginBottom('paddingBottom');
    if (isWeb) {
        return (<ScrollView style={[styles.accountMenu, style]}>{content}</ScrollView>);
    }
    return (<ModalScrollView onScroll={onScroll} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} style={style} contentContainerStyle={paddingBottom}>
      {content}
    </ModalScrollView>);
};
const styleSet = createStyleSheets((colors) => ({
    disabled: {
        opacity: 0.5,
    },
    modalContentContainer: {
        paddingHorizontal: isWeb ? undefined : 0,
    },
    accountMenu: {
        ...(isWeb && {
            padding: rem(8),
            backgroundColor: colors.cards.onDark,
        }),
    },
}));
export default memo(AccountMenu);
